import { useId, useState, type ReactNode } from "react";
import type { FeatureKey } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { permissionCatalogQuery, useUpdateCatalogEntry } from "../../api/queries";
import { Button } from "../../design/components/Button";
import { Checkbox } from "../../design/components/Checkbox";
import { Dialog } from "../../design/components/Dialog";
import { Reason } from "../../design/components/Reason";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import { FEATURE_GROUPS, FEATURE_LABEL } from "./permission-labels";
import "./roles.css";

// Ruoli e permessi, scheda «Per tutte le aziende» (piano M1b Task 9, mock-up `permessi.html`): soltanto per il
// Super admin di Gioia365, fuori dal contesto di un'azienda (`/api/platform/permission-catalog`, senza header).
// Spegnere una funzione la toglie a ogni ruolo non di sistema di ogni azienda e riaccenderla non la rimette: per
// questo chiede conferma. Riaccenderla agisce subito. La conferma si chiude prima dell'avviso (D16).

export function PlatformCatalogTab() {
  const t = useT();
  const titleId = useId();
  const toast = useToast();
  const catalog = useQuery(permissionCatalogQuery);
  const update = useUpdateCatalogEntry();
  const [confirming, setConfirming] = useState<FeatureKey | null>(null);
  // La funzione in attesa del server: il suo interruttore resta spento finché non risponde.
  const [pending, setPending] = useState<FeatureKey | null>(null);

  const label = (key: FeatureKey) => t(FEATURE_LABEL[key]);

  async function apply(key: FeatureKey, availableToOrganizations: boolean) {
    setConfirming(null);
    setPending(key);
    try {
      const { rolesUpdated } = await update.mutateAsync({ key, availableToOrganizations });
      const feature = label(key);
      let message: string;
      if (availableToOrganizations) message = t("roles.platform.restored", { feature });
      else if (rolesUpdated === 1) message = t("roles.platform.removedOne", { feature });
      else message = t("roles.platform.removed", { feature, count: rolesUpdated });
      toast.show(message, "success");
    } catch (error) {
      toast.show(messageForError(t, error), "danger");
    } finally {
      setPending(null);
    }
  }

  function toggle(key: FeatureKey, checked: boolean) {
    if (checked) void apply(key, true);
    else setConfirming(key);
  }

  const entries = catalog.data;
  let content: ReactNode;
  if (!entries && catalog.isError) {
    content = (
      <div className="g-roles__problem">
        <Reason tone="warning" role="alert">{messageForError(t, catalog.error)}</Reason>
        <Button onClick={() => void catalog.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else if (!entries) {
    content = <p className="g-roles__muted">{t("common.loading")}</p>;
  } else {
    const available = new Map(entries.map((entry) => [entry.key, entry.availableToOrganizations]));
    content = (
      <div className="g-roles__features">
        {FEATURE_GROUPS.map((group) => (
          <fieldset key={group.title} className="g-roles__group">
            <legend>{t(group.title)}</legend>
            {group.keys.map((key) => {
              const current = available.get(key);
              // Una voce che il server non conosce ancora non si può cambiare da qui.
              if (current === undefined) return null;
              return (
                <Checkbox
                  key={key}
                  id={`${titleId}-${key}`}
                  label={label(key)}
                  checked={current}
                  disabled={pending === key}
                  aria-busy={pending === key ? "true" : undefined}
                  onChange={(checked) => toggle(key, checked)}
                />
              );
            })}
          </fieldset>
        ))}
      </div>
    );
  }

  return (
    <section className="g-roles__section" aria-labelledby={titleId}>
      <div className="g-roles__head">
        <h2 id={titleId}>{t("roles.platform.title")}</h2>
        <p className="g-roles__muted">{t("roles.platform.intro")}</p>
      </div>
      {content}
      {confirming ? (
        <Dialog
          open
          title={t("roles.platform.removeTitle", { feature: label(confirming) })}
          onClose={() => setConfirming(null)}
          footer={
            <>
              <Button onClick={() => setConfirming(null)}>{t("common.cancel")}</Button>
              <Button variant="primary" onClick={() => void apply(confirming, false)}>
                {t("roles.platform.removeConfirm")}
              </Button>
            </>
          }
        >
          <p>{t("roles.platform.removeText")}</p>
        </Dialog>
      ) : null}
    </section>
  );
}
